/**
 * Painel de controles centralizado no topo
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Stack,
  Typography,
  Chip,
  SelectChangeEvent,
  LinearProgress,
  ListSubheader,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Collapse,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ClearIcon from '@mui/icons-material/Clear';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import MapIcon from '@mui/icons-material/Map';
import TableChartIcon from '@mui/icons-material/TableChart';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import {
  setRadius,
  setIsSelectingLocation,
  setSelectedLocation,
  setCenter
} from '../../store/slices/mapSlice';
import {
  startSearch,
  searchSuccess,
  searchError,
  clearResults
} from '../../store/slices/searchSlice';
import { BusinessSector, BusinessSectorLabels, SearchParams } from '../../types';
import { config } from '../../utils/config';
import { googlePlacesService } from '../../services/googlePlacesService';

interface ControlPanelProps {
  onSearchStart?: () => void;
  onSearchData?: (data: { city?: string; neighborhood?: string; sector?: string }) => void;
  sortBy: 'name' | 'rating' | 'distance';
  onSortChange: (value: 'name' | 'rating' | 'distance') => void;
  filterQuality: number;
  onFilterQualityChange: (value: number) => void;
  filterHasWebsite: boolean;
  onFilterWebsiteChange: (value: boolean) => void;
  filterHasWhatsapp: boolean;
  onFilterWhatsappChange: (value: boolean) => void;
  filterHasPhone: boolean;
  onFilterPhoneChange: (value: boolean) => void;
  filterHasEmail: boolean;
  onFilterEmailChange: (value: boolean) => void;
  activeTab: number;
  onTabChange: (value: number) => void;
  onExportCSV?: () => void;
  onExportJSON?: () => void;
  resultsCount: number;
  selectedCount: number;
}

const ControlPanel: React.FC<ControlPanelProps> = ({
  onSearchStart,
  onSearchData,
  sortBy,
  onSortChange,
  filterQuality,
  onFilterQualityChange,
  filterHasWebsite,
  onFilterWebsiteChange,
  filterHasWhatsapp,
  onFilterWhatsappChange,
  filterHasPhone,
  onFilterPhoneChange,
  filterHasEmail,
  onFilterEmailChange,
  activeTab,
  onTabChange,
  onExportCSV,
  onExportJSON,
  resultsCount,
  selectedCount
}) => {
  const dispatch = useAppDispatch();
  const { selectedLocation, radius, isSelectingLocation } = useAppSelector(
    (state) => state.map
  );
  const { isSearching, results } = useAppSelector((state) => state.search);

  const [sector, setSector] = useState<BusinessSector>(BusinessSector.LOJA_ROUPA);
  const [expanded, setExpanded] = useState<boolean>(true);

  const handleSectorChange = (event: SelectChangeEvent) => {
    setSector(event.target.value as BusinessSector);
  };

  const handleRadiusChange = (_event: Event, newValue: number | number[]) => {
    dispatch(setRadius(newValue as number));
  };

  const handleSelectLocation = () => {
    dispatch(setIsSelectingLocation(!isSelectingLocation));
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          dispatch(setSelectedLocation(coordinates));
          dispatch(setCenter(coordinates));
        },
        (error) => {
          console.error('Erro ao obter localização:', error);
          alert('Não foi possível obter sua localização. Verifique as permissões do navegador.');
        }
      );
    } else {
      alert('Geolocalização não é suportada pelo seu navegador.');
    }
  };

  const handleSearch = async () => {
    if (!selectedLocation) {
      alert('Selecione um ponto no mapa primeiro');
      return;
    }

    const searchParams: SearchParams = {
      center: selectedLocation,
      radius,
      sector
    };

    dispatch(startSearch(searchParams));
    
    if (onSearchStart) {
      onSearchStart();
    }

    try {
      const businesses = await googlePlacesService.searchNearby(
        selectedLocation,
        radius,
        sector
      );
      dispatch(searchSuccess(businesses));

      // Enviar dados da busca para geração automática de nome
      if (onSearchData && businesses.length > 0) {
        // Extrair cidade e bairro do primeiro resultado (mais comum)
        const firstBusiness = businesses[0];
        onSearchData({
          city: firstBusiness.city || '',
          neighborhood: firstBusiness.neighborhood || '',
          sector: BusinessSectorLabels[sector] || ''
        });
      }
    } catch (error) {
      console.error('Erro na busca:', error);
      dispatch(searchError('Erro ao buscar estabelecimentos. Tente novamente.'));
    }
  };

  const handleClear = () => {
    dispatch(clearResults());
    dispatch(setSelectedLocation(undefined));
    dispatch(setIsSelectingLocation(false));
  };

  const formatRadius = (value: number): string => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)} km`;
    }
    return `${value} m`;
  };

  return (
    <Paper elevation={3} sx={{ p: 2 }}>
      <Stack spacing={2}>
        {/* HEADER COM BOTÃO DE EXPANSÃO */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            📍 Localização e Parâmetros de Busca
          </Typography>
          
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Mostrar status quando contraído */}
            {!expanded && selectedLocation && (
              <Chip
                label={`📍 ${selectedLocation.lat.toFixed(4)}, ${selectedLocation.lng.toFixed(4)}`}
                color="primary"
                size="small"
              />
            )}
            {!expanded && results.length > 0 && (
              <Chip
                label={`✅ ${results.length} resultados`}
                color="success"
                size="small"
              />
            )}
            {!expanded && (
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={!selectedLocation || isSearching}
                size="small"
              >
                Buscar
              </Button>
            )}
            
            <Tooltip title={expanded ? "Minimizar controles" : "Expandir controles"}>
              <IconButton
                onClick={() => setExpanded(!expanded)}
                size="small"
                color="primary"
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* CONTROLES EXPANSÍVEIS */}
        <Collapse in={expanded}>
          <Stack spacing={2}>
            {/* SEÇÃO 1: LOCALIZAÇÃO E PARÂMETROS DE BUSCA */}
            <Stack 
              direction="row" 
              spacing={2} 
              alignItems="center"
              sx={{ 
                overflowX: 'auto',
                overflowY: 'hidden',
                '&::-webkit-scrollbar': {
                  height: 6,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: 3,
                }
              }}
            >
            <Button
              variant={isSelectingLocation ? 'contained' : 'outlined'}
              startIcon={<MyLocationIcon />}
              onClick={handleSelectLocation}
              color={isSelectingLocation ? 'success' : 'primary'}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              {isSelectingLocation ? 'Clique no Mapa' : 'Selecionar Local'}
            </Button>

            <Button
              variant="outlined"
              startIcon={<MyLocationIcon />}
              onClick={handleGetCurrentLocation}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              Minha Localização
            </Button>

            {selectedLocation && (
              <Chip
                label={`📍 ${selectedLocation.lat.toFixed(4)}, ${selectedLocation.lng.toFixed(4)}`}
                color="primary"
                size="small"
                onDelete={() => dispatch(setSelectedLocation(undefined))}
                sx={{ flexShrink: 0 }}
              />
            )}

            <Divider orientation="vertical" flexItem sx={{ mx: 1, flexShrink: 0 }} />

            <FormControl sx={{ minWidth: 200, flexShrink: 0 }} size="small">
              <InputLabel id="sector-label">Setor</InputLabel>
              <Select
                labelId="sector-label"
                value={sector}
                label="Setor"
                onChange={handleSectorChange}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                    },
                  },
                }}
              >
                <MenuItem value={BusinessSector.TODOS}>
                  {BusinessSectorLabels[BusinessSector.TODOS]}
                </MenuItem>
                
                <ListSubheader>🍽️ ALIMENTAÇÃO E BEBIDAS</ListSubheader>
                <MenuItem value={BusinessSector.RESTAURANTE}>{BusinessSectorLabels[BusinessSector.RESTAURANTE]}</MenuItem>
                <MenuItem value={BusinessSector.PIZZARIA}>{BusinessSectorLabels[BusinessSector.PIZZARIA]}</MenuItem>
                <MenuItem value={BusinessSector.HAMBURGUERIA}>{BusinessSectorLabels[BusinessSector.HAMBURGUERIA]}</MenuItem>
                <MenuItem value={BusinessSector.SUSHI}>{BusinessSectorLabels[BusinessSector.SUSHI]}</MenuItem>
                <MenuItem value={BusinessSector.RESTAURANTE_CHINES}>{BusinessSectorLabels[BusinessSector.RESTAURANTE_CHINES]}</MenuItem>
                <MenuItem value={BusinessSector.RESTAURANTE_ITALIANO}>{BusinessSectorLabels[BusinessSector.RESTAURANTE_ITALIANO]}</MenuItem>
                <MenuItem value={BusinessSector.RESTAURANTE_MEXICANO}>{BusinessSectorLabels[BusinessSector.RESTAURANTE_MEXICANO]}</MenuItem>
                <MenuItem value={BusinessSector.CHURRASCARIA}>{BusinessSectorLabels[BusinessSector.CHURRASCARIA]}</MenuItem>
                <MenuItem value={BusinessSector.RESTAURANTE_VEGETARIANO}>{BusinessSectorLabels[BusinessSector.RESTAURANTE_VEGETARIANO]}</MenuItem>
                <MenuItem value={BusinessSector.CAFE}>{BusinessSectorLabels[BusinessSector.CAFE]}</MenuItem>
                <MenuItem value={BusinessSector.PADARIA}>{BusinessSectorLabels[BusinessSector.PADARIA]}</MenuItem>
                <MenuItem value={BusinessSector.BAR}>{BusinessSectorLabels[BusinessSector.BAR]}</MenuItem>
                <MenuItem value={BusinessSector.LANCHONETE}>{BusinessSectorLabels[BusinessSector.LANCHONETE]}</MenuItem>
                <MenuItem value={BusinessSector.SORVETERIA}>{BusinessSectorLabels[BusinessSector.SORVETERIA]}</MenuItem>
                <MenuItem value={BusinessSector.DOCERIA}>{BusinessSectorLabels[BusinessSector.DOCERIA]}</MenuItem>
                <MenuItem value={BusinessSector.ROTISSERIA}>{BusinessSectorLabels[BusinessSector.ROTISSERIA]}</MenuItem>
                <MenuItem value={BusinessSector.ACOUGUE}>{BusinessSectorLabels[BusinessSector.ACOUGUE]}</MenuItem>
                <MenuItem value={BusinessSector.PEIXARIA}>{BusinessSectorLabels[BusinessSector.PEIXARIA]}</MenuItem>
                <MenuItem value={BusinessSector.HORTIFRUTI}>{BusinessSectorLabels[BusinessSector.HORTIFRUTI]}</MenuItem>
                <MenuItem value={BusinessSector.ADEGA}>{BusinessSectorLabels[BusinessSector.ADEGA]}</MenuItem>
                
                <ListSubheader>🛍️ COMPRAS E VAREJO</ListSubheader>
                <MenuItem value={BusinessSector.LOJA}>{BusinessSectorLabels[BusinessSector.LOJA]}</MenuItem>
                <MenuItem value={BusinessSector.SHOPPING}>{BusinessSectorLabels[BusinessSector.SHOPPING]}</MenuItem>
                <MenuItem value={BusinessSector.SUPERMERCADO}>{BusinessSectorLabels[BusinessSector.SUPERMERCADO]}</MenuItem>
                <MenuItem value={BusinessSector.CONVENIENCIA}>{BusinessSectorLabels[BusinessSector.CONVENIENCIA]}</MenuItem>
                <MenuItem value={BusinessSector.LOJA_ROUPA}>{BusinessSectorLabels[BusinessSector.LOJA_ROUPA]}</MenuItem>
                <MenuItem value={BusinessSector.LOJA_CALCADOS}>{BusinessSectorLabels[BusinessSector.LOJA_CALCADOS]}</MenuItem>
                <MenuItem value={BusinessSector.OTICA}>{BusinessSectorLabels[BusinessSector.OTICA]}</MenuItem>
                <MenuItem value={BusinessSector.JOALHERIA}>{BusinessSectorLabels[BusinessSector.JOALHERIA]}</MenuItem>
                <MenuItem value={BusinessSector.LOJA_ELETRONICOS}>{BusinessSectorLabels[BusinessSector.LOJA_ELETRONICOS]}</MenuItem>
                <MenuItem value={BusinessSector.LOJA_INFORMATICA}>{BusinessSectorLabels[BusinessSector.LOJA_INFORMATICA]}</MenuItem>
                <MenuItem value={BusinessSector.LOJA_LIVROS}>{BusinessSectorLabels[BusinessSector.LOJA_LIVROS]}</MenuItem>
                <MenuItem value={BusinessSector.PAPELARIA}>{BusinessSectorLabels[BusinessSector.PAPELARIA]}</MenuItem>
                <MenuItem value={BusinessSector.BRINQUEDOS}>{BusinessSectorLabels[BusinessSector.BRINQUEDOS]}</MenuItem>
                <MenuItem value={BusinessSector.ARTIGOS_ESPORTE}>{BusinessSectorLabels[BusinessSector.ARTIGOS_ESPORTE]}</MenuItem>
                <MenuItem value={BusinessSector.PET_SHOP}>{BusinessSectorLabels[BusinessSector.PET_SHOP]}</MenuItem>
                <MenuItem value={BusinessSector.FLORICULTURA}>{BusinessSectorLabels[BusinessSector.FLORICULTURA]}</MenuItem>
                <MenuItem value={BusinessSector.FARMACIA}>{BusinessSectorLabels[BusinessSector.FARMACIA]}</MenuItem>
                <MenuItem value={BusinessSector.PERFUMARIA}>{BusinessSectorLabels[BusinessSector.PERFUMARIA]}</MenuItem>
                <MenuItem value={BusinessSector.COSMETICOS}>{BusinessSectorLabels[BusinessSector.COSMETICOS]}</MenuItem>
                <MenuItem value={BusinessSector.BOUTIQUE}>{BusinessSectorLabels[BusinessSector.BOUTIQUE]}</MenuItem>
                <MenuItem value={BusinessSector.LOJA_BOLSAS}>{BusinessSectorLabels[BusinessSector.LOJA_BOLSAS]}</MenuItem>
                <MenuItem value={BusinessSector.LOJA_GAMES}>{BusinessSectorLabels[BusinessSector.LOJA_GAMES]}</MenuItem>
                
                <ListSubheader>💼 SERVIÇOS E BELEZA</ListSubheader>
                <MenuItem value={BusinessSector.CABELEIREIRO}>{BusinessSectorLabels[BusinessSector.CABELEIREIRO]}</MenuItem>
                <MenuItem value={BusinessSector.BARBEARIA}>{BusinessSectorLabels[BusinessSector.BARBEARIA]}</MenuItem>
                <MenuItem value={BusinessSector.MANICURE}>{BusinessSectorLabels[BusinessSector.MANICURE]}</MenuItem>
                <MenuItem value={BusinessSector.BELEZA}>{BusinessSectorLabels[BusinessSector.BELEZA]}</MenuItem>
                <MenuItem value={BusinessSector.ESTETICA}>{BusinessSectorLabels[BusinessSector.ESTETICA]}</MenuItem>
                <MenuItem value={BusinessSector.TATUAGEM}>{BusinessSectorLabels[BusinessSector.TATUAGEM]}</MenuItem>
                <MenuItem value={BusinessSector.SPA}>{BusinessSectorLabels[BusinessSector.SPA]}</MenuItem>
                <MenuItem value={BusinessSector.ACADEMIA}>{BusinessSectorLabels[BusinessSector.ACADEMIA]}</MenuItem>
                <MenuItem value={BusinessSector.LAVANDERIA}>{BusinessSectorLabels[BusinessSector.LAVANDERIA]}</MenuItem>
                <MenuItem value={BusinessSector.FOTOGRAFIA}>{BusinessSectorLabels[BusinessSector.FOTOGRAFIA]}</MenuItem>
                <MenuItem value={BusinessSector.FOTOGRAFO}>{BusinessSectorLabels[BusinessSector.FOTOGRAFO]}</MenuItem>
                
                <ListSubheader>🚗 AUTOMOTIVO</ListSubheader>
                <MenuItem value={BusinessSector.OFICINA}>{BusinessSectorLabels[BusinessSector.OFICINA]}</MenuItem>
                <MenuItem value={BusinessSector.POSTO_GASOLINA}>{BusinessSectorLabels[BusinessSector.POSTO_GASOLINA]}</MenuItem>
                <MenuItem value={BusinessSector.LAVAGEM_CARRO}>{BusinessSectorLabels[BusinessSector.LAVAGEM_CARRO]}</MenuItem>
                <MenuItem value={BusinessSector.BORRACHARIA}>{BusinessSectorLabels[BusinessSector.BORRACHARIA]}</MenuItem>
                <MenuItem value={BusinessSector.FUNILARIA}>{BusinessSectorLabels[BusinessSector.FUNILARIA]}</MenuItem>
                <MenuItem value={BusinessSector.CONCESSIONARIA}>{BusinessSectorLabels[BusinessSector.CONCESSIONARIA]}</MenuItem>
                <MenuItem value={BusinessSector.INSULFILM}>{BusinessSectorLabels[BusinessSector.INSULFILM]}</MenuItem>
                <MenuItem value={BusinessSector.SOM_AUTOMOTIVO}>{BusinessSectorLabels[BusinessSector.SOM_AUTOMOTIVO]}</MenuItem>
                <MenuItem value={BusinessSector.DESPACHANTE_VEICULAR}>{BusinessSectorLabels[BusinessSector.DESPACHANTE_VEICULAR]}</MenuItem>
                
                <ListSubheader>🏥 SAÚDE</ListSubheader>
                <MenuItem value={BusinessSector.HOSPITAL}>{BusinessSectorLabels[BusinessSector.HOSPITAL]}</MenuItem>
                <MenuItem value={BusinessSector.CLINICA}>{BusinessSectorLabels[BusinessSector.CLINICA]}</MenuItem>
                <MenuItem value={BusinessSector.DENTISTA}>{BusinessSectorLabels[BusinessSector.DENTISTA]}</MenuItem>
                <MenuItem value={BusinessSector.FISIOTERAPEUTA}>{BusinessSectorLabels[BusinessSector.FISIOTERAPEUTA]}</MenuItem>
                <MenuItem value={BusinessSector.PSICOLOGO}>{BusinessSectorLabels[BusinessSector.PSICOLOGO]}</MenuItem>
                <MenuItem value={BusinessSector.NUTRICIONISTA}>{BusinessSectorLabels[BusinessSector.NUTRICIONISTA]}</MenuItem>
                <MenuItem value={BusinessSector.MASSAGEM}>{BusinessSectorLabels[BusinessSector.MASSAGEM]}</MenuItem>
                <MenuItem value={BusinessSector.VETERINARIO}>{BusinessSectorLabels[BusinessSector.VETERINARIO]}</MenuItem>
                <MenuItem value={BusinessSector.LABORATORIO}>{BusinessSectorLabels[BusinessSector.LABORATORIO]}</MenuItem>
                
                <ListSubheader>⚖️ SERVIÇOS PROFISSIONAIS</ListSubheader>
                <MenuItem value={BusinessSector.ADVOGADO}>{BusinessSectorLabels[BusinessSector.ADVOGADO]}</MenuItem>
                <MenuItem value={BusinessSector.CONTADOR}>{BusinessSectorLabels[BusinessSector.CONTADOR]}</MenuItem>
                <MenuItem value={BusinessSector.ARQUITETO}>{BusinessSectorLabels[BusinessSector.ARQUITETO]}</MenuItem>
                <MenuItem value={BusinessSector.IMOBILIARIA}>{BusinessSectorLabels[BusinessSector.IMOBILIARIA]}</MenuItem>
                <MenuItem value={BusinessSector.CORRETOR}>{BusinessSectorLabels[BusinessSector.CORRETOR]}</MenuItem>
                
                <ListSubheader>🎬 ENTRETENIMENTO</ListSubheader>
                <MenuItem value={BusinessSector.CINEMA}>{BusinessSectorLabels[BusinessSector.CINEMA]}</MenuItem>
                <MenuItem value={BusinessSector.BOLICHE}>{BusinessSectorLabels[BusinessSector.BOLICHE]}</MenuItem>
                <MenuItem value={BusinessSector.KARAOKE}>{BusinessSectorLabels[BusinessSector.KARAOKE]}</MenuItem>
                <MenuItem value={BusinessSector.BILHAR}>{BusinessSectorLabels[BusinessSector.BILHAR]}</MenuItem>
                <MenuItem value={BusinessSector.FLIPERAMA}>{BusinessSectorLabels[BusinessSector.FLIPERAMA]}</MenuItem>
                <MenuItem value={BusinessSector.ESCAPE_ROOM}>{BusinessSectorLabels[BusinessSector.ESCAPE_ROOM]}</MenuItem>
                
                <ListSubheader>🔧 TÉCNICOS</ListSubheader>
                <MenuItem value={BusinessSector.ELETRICISTA}>{BusinessSectorLabels[BusinessSector.ELETRICISTA]}</MenuItem>
                <MenuItem value={BusinessSector.ENCANADOR}>{BusinessSectorLabels[BusinessSector.ENCANADOR]}</MenuItem>
                <MenuItem value={BusinessSector.PINTOR}>{BusinessSectorLabels[BusinessSector.PINTOR]}</MenuItem>
                <MenuItem value={BusinessSector.CHAVEIRO}>{BusinessSectorLabels[BusinessSector.CHAVEIRO]}</MenuItem>
                <MenuItem value={BusinessSector.ASSISTENCIA_TECNICA}>{BusinessSectorLabels[BusinessSector.ASSISTENCIA_TECNICA]}</MenuItem>
                <MenuItem value={BusinessSector.ASSISTENCIA_CELULAR}>{BusinessSectorLabels[BusinessSector.ASSISTENCIA_CELULAR]}</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ minWidth: 200, maxWidth: 300, flexShrink: 0 }}>
              <Typography variant="body2" gutterBottom>
                Raio: {formatRadius(radius)}
              </Typography>
              <Slider
                value={radius}
                onChange={handleRadiusChange}
                min={config.minRadius}
                max={config.maxRadius}
                step={500}
                valueLabelDisplay="auto"
                valueLabelFormat={formatRadius}
                size="small"
              />
            </Box>

            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={!selectedLocation || isSearching}
              size="large"
              sx={{ minWidth: 150, flexShrink: 0 }}
            >
              {isSearching ? 'Buscando...' : 'Buscar'}
            </Button>

            {(selectedLocation || results.length > 0) && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ClearIcon />}
                onClick={handleClear}
                size="small"
                sx={{ flexShrink: 0 }}
              >
                Limpar
              </Button>
            )}
          </Stack>

          <Divider />

          {/* SEÇÃO 2: FILTROS, ORDENAÇÃO E NAVEGAÇÃO */}
          <Stack 
            direction="row" 
            spacing={2} 
            alignItems="center" 
            sx={{ 
              overflowX: 'auto',
              overflowY: 'hidden',
              '&::-webkit-scrollbar': {
                height: 6,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: 3,
              }
            }}
          >
            {/* Tabs de Navegação */}
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => onTabChange(newValue)}
              sx={{ 
                minHeight: 40,
                flexShrink: 0,
                '& .MuiTab-root': { 
                  minHeight: 40, 
                  py: 0.5,
                  fontSize: '0.875rem'
                }
              }}
            >
              <Tab 
                label="Mapa" 
                icon={<MapIcon fontSize="small" />} 
                iconPosition="start"
                value={0}
              />
              <Tab 
                label="Tabela" 
                icon={<TableChartIcon fontSize="small" />} 
                iconPosition="start"
                value={1}
              />
            </Tabs>

            <Divider orientation="vertical" flexItem sx={{ flexShrink: 0 }} />

            <TextField
              select
              label="Ordenar por"
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value as any)}
              size="small"
              sx={{ minWidth: 140, flexShrink: 0 }}
            >
              <MenuItem value="name">Nome</MenuItem>
              <MenuItem value="rating">Avaliação</MenuItem>
              <MenuItem value="distance">Distância</MenuItem>
            </TextField>

            <TextField
              select
              label="Qualidade mínima"
              value={filterQuality}
              onChange={(e) => onFilterQualityChange(Number(e.target.value))}
              size="small"
              sx={{ minWidth: 140, flexShrink: 0 }}
            >
              <MenuItem value={0}>Todas</MenuItem>
              <MenuItem value={40}>Mínima (40%)</MenuItem>
              <MenuItem value={70}>Boa (70%)</MenuItem>
              <MenuItem value={90}>Excelente (90%)</MenuItem>
            </TextField>

            <Divider orientation="vertical" flexItem sx={{ flexShrink: 0 }} />

            <FormGroup row sx={{ flexShrink: 0 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterHasWebsite}
                    onChange={(e) => onFilterWebsiteChange(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <LanguageIcon fontSize="small" />
                    <Typography variant="body2">Website</Typography>
                  </Stack>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterHasWhatsapp}
                    onChange={(e) => onFilterWhatsappChange(e.target.checked)}
                    size="small"
                    color="success"
                  />
                }
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <WhatsAppIcon fontSize="small" color="success" />
                    <Typography variant="body2">WhatsApp</Typography>
                  </Stack>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterHasPhone}
                    onChange={(e) => onFilterPhoneChange(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PhoneIcon fontSize="small" />
                    <Typography variant="body2">Telefone</Typography>
                  </Stack>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterHasEmail}
                    onChange={(e) => onFilterEmailChange(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <EmailIcon fontSize="small" />
                    <Typography variant="body2">Email</Typography>
                  </Stack>
                }
              />
            </FormGroup>

            <Divider orientation="vertical" flexItem sx={{ flexShrink: 0 }} />

            {/* Botões de Exportação */}
            <Stack direction="row" spacing={1} sx={{ ml: 'auto', flexShrink: 0 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={onExportCSV}
                disabled={resultsCount === 0}
              >
                CSV ({selectedCount > 0 ? selectedCount : resultsCount})
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={onExportJSON}
                disabled={resultsCount === 0}
              >
                JSON ({selectedCount > 0 ? selectedCount : resultsCount})
              </Button>
            </Stack>
          </Stack>

          {/* Barra de progresso */}
          {isSearching && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Buscando estabelecimentos e coletando informações de contato...
              </Typography>
            </Box>
          )}
        </Stack>
      </Collapse>
      </Stack>
    </Paper>
  );
};

export default ControlPanel;

